import chevironRight from "$/cheviron-right.svg";
import a1 from "$/a1.svg";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Dialog } from "@/Dialog";
import { ContractTreeChildren } from "@/business/contract/ContractTreeChildren";
import { useEffect, useState } from "react";
import {
  useBusinessAuthUserInfo,
  useReadBusinessActiveAlarm,
} from "~/services/business/auth";
import {
  useBusinessMypageMain,
  useRequestCompanyActivation,
} from "~/services/business/myPage";
import { useBusinessAnnouncementUnreadCount } from "~/services/business/announcement";
import { openWithHandoffIfApp } from "%/handoff";
import { useCompanyCreditWallet } from "~/services/business/credit";
import { CreditWalletCard } from "@/business/credit/CreditWalletCard";

export const Route = createFileRoute("/business/layout/user-owner-setting")({
  component: UserSetting,
});

function UserSetting() {
  const { history } = useRouter();
  const [contractMenuOpen, setContractMenuOpen] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isActivationSuccessVisible, setIsActivationSuccessVisible] =
    useState(false);
  const [isActivationErrorVisible, setIsActivationErrorVisible] =
    useState(false);
  const [activationMessage, setActivationMessage] = useState<string>("");
  const { data: mypageMain } = useBusinessMypageMain();
  const {
    mutateAsync: requestActivationAsync,
    isPending: isActivationPending,
  } = useRequestCompanyActivation();
  const { data: businessUserInfo } = useBusinessAuthUserInfo(true);
  const { mutateAsync: readActiveAlarmAsync } = useReadBusinessActiveAlarm();
  const { data: announcementUnread } = useBusinessAnnouncementUnreadCount();
  const announcementUnreadCount = announcementUnread?.count ?? 0;
  const { data: creditWallet } = useCompanyCreditWallet();
  const [isActivationApprovedVisible, setIsActivationApprovedVisible] =
    useState(false);
  const [hasShownActivationApproved, setHasShownActivationApproved] =
    useState(false);

  const isAgreed = mypageMain?.activeStatus === "AGREED";

  useEffect(() => {
    if (businessUserInfo?.activeCompany && !hasShownActivationApproved) {
      setIsActivationApprovedVisible(true);
    }
  }, [businessUserInfo?.activeCompany, hasShownActivationApproved]);

  // const handleModalOpen = () => {
  //   setIsModalVisible(true);
  // };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleCompanyActivation = async () => {
    try {
      const response = await requestActivationAsync();
      if (response.statusCode === 200) {
        setActivationMessage("활성화 요청을 했습니다.");
        setIsActivationSuccessVisible(true);
      } else {
        setActivationMessage(response.message || "활성화 요청에 실패했습니다.");
        setIsActivationErrorVisible(true);
      }
    } catch (error: any) {
      console.error("활성화 요청 실패:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "활성화 요청 중 오류가 발생했습니다.";
      setActivationMessage(errorMsg);
      setIsActivationErrorVisible(true);
    }
  };

  const handleActivationApprovedConfirm = async () => {
    try {
      await readActiveAlarmAsync();
    } catch (error) {
      console.error("활성화 승인 알림 읽음 처리 실패:", error);
    } finally {
      setHasShownActivationApproved(true);
      setIsActivationApprovedVisible(false);
    }
  };

  // console.log(mypageMain)
  return (
    <>
      <LayoutHeader title={"설정"} hasFootPrint={false} />
      <div className="flex-auto px-3 bg-[#F5F5F5]">
        <div className="bg-[#F5F5F5]">
          {/* 상호명 — 일반 텍스트 줄 (카드 아님) */}
          <div className="flex items-center gap-3 px-1 pt-4 pb-3">
            <img
              src={mypageMain?.fileUrl ?? a1}
              alt="유치원 이미지"
              className="rounded-full w-[44px] h-[44px] bg-gray-50 shrink-0 object-cover"
            />
            <div className="min-w-0">
              <p className="text-[16px] font-bold text-[#111] truncate">
                {mypageMain?.companyName ?? "고객"}
              </p>
              <p className="text-[12px] text-[#888] truncate">
                원장 · {mypageMain?.bizUserName ?? "고객"}
              </p>
            </div>
          </div>

          {/* 크레딧 카드 — 보유 크레딧 + 충전하기 + 구매/무료 분리 */}
          <div className="mb-4">
            <CreditWalletCard
              wallet={creditWallet}
              // 크레딧 조회는 앱 내(webview) 이동 — 외부 브라우저/handoff 불필요
              onCardClick={() =>
                history.push("/business/layout/user-credit-wallet" as any)
              }
              // 충전(결제)은 PG 흐름이라 기존대로 웹(handoff) 유지
              onChargeClick={() =>
                openWithHandoffIfApp(
                  "/business/layout/user-credit-wallet-charge",
                  () =>
                    history.push(
                      "/business/layout/user-credit-wallet-charge" as any,
                    ),
                )
              }
            />
          </div>

          {mypageMain && !isAgreed && (
            <div className="my-4">
              <div className="bg-white border-2 border-[#E9E9E9] rounded-xl pl-1 pr-4 pb-3 pt-1">
                <div className="flex justify-start">
                  <div className="flex justify-center items-center w-5 h-5 text-sm font-bold text-white bg-orange-400 rounded-full">
                    !
                  </div>
                </div>
                {mypageMain?.activeStatus === "NOT_REQUESTED" && (
                  <>
                    <p className="mb-2 font-medium text-center">
                      현재 테스트모드로 동작 중입니다.
                    </p>
                    <div className="mb-1 font-medium text-center">
                      테스트 모드에서도 대부분 기능을 정상적으로 이용하실 수
                      있습니다.
                    </div>
                    <div className="mb-1 font-medium text-center">
                      현재 이용권의 최대 발급 수량이 20건으로 제한되어 있으며,
                    </div>
                    <div className="mb-1 font-medium text-center">
                      매장정보에 테스트모드라는 점이 고지됩니다.
                    </div>
                    <div className="mb-2 font-medium text-center">
                      서비스가 활성화 되면 모든 기능이 정상 동작합니다.
                    </div>
                    <button
                      className="py-2 ml-1.5 w-full font-semibold text-white bg-orange-400 rounded-full disabled:opacity-50"
                      onClick={handleCompanyActivation}
                      disabled={isActivationPending}
                    >
                      {isActivationPending ? "요청 중..." : "활성화 요청"}
                    </button>
                  </>
                )}
                {mypageMain?.activeStatus === "REQUESTED" && (
                  <div className="mb-1 font-medium text-center">
                    서비스 활성화 요청 진행중입니다.
                  </div>
                )}
                {mypageMain?.activeStatus === "DENIED" && (
                  <>
                    <div className="mb-1 font-medium text-center">
                      서비스 활성화 요청이 반려 되었습니다.
                    </div>
                    <div className="mb-2 font-medium text-center">
                      가입하신 이메일에서 사유를 확인 후 다시 요청해주세요.
                    </div>
                    <button
                      className="py-2 ml-1.5 w-full font-semibold text-white bg-orange-400 rounded-full disabled:opacity-50"
                      onClick={handleCompanyActivation}
                      disabled={isActivationPending}
                    >
                      {isActivationPending ? "요청 중..." : "활성화 요청"}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {/* 사용자 설정 */}
          <div>
            <div className="mb-2 font-medium">서비스 설정</div>
            <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
              {/* <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={handleModalOpen}
              >
                <span>서비스 플랜</span>
                <div className="flex gap-2 items-center">
                  <span className="text-[#666666] text-[13px]">베타서비스</span>
                  <img src={chevironRight} alt="사용자 설정" />
                </div>
              </button> */}
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() => history.push("/business/layout/user-owner-push")}
              >
                <span>알림 설정</span>
                <img src={chevironRight} alt="알림" />
              </button>
            </div>
          </div>
          <div className="mt-4">
            <div className="mb-2 font-medium">계정 설정</div>
            <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() => history.push("/business/layout/user-owner-info")}
              >
                <span>계정 정보 변경</span>
                <img src={chevironRight} alt="계정 정보 변경" />
              </button>
            </div>
          </div>

          {/* 사업자 설정 */}
          <div className="mt-4 mb-6">
            <div className="mb-2 font-medium">사업자 설정</div>
            <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
              <button
                className="flex justify-between items-center px-4 py-3 w-full"
                onClick={() =>
                  history.push("/business/layout/user-business-info")
                }
              >
                <span>사업자 정보 관리</span>
                <img src={chevironRight} alt="사업자 정보 관리" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-employee-management")
                }
              >
                <span>임직원 정보 관리</span>
                <img src={chevironRight} alt="임직원 정보 관리" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-service-statistics")
                }
              >
                <span>서비스 통계</span>
                <img src={chevironRight} alt="서비스 통계" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-customer-management")
                }
              >
                <span>고객 정보 목록</span>
                <img src={chevironRight} alt="고객 정보 목록" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() => history.push("/business/layout/notify-style")}
              >
                <span>내 알림장 스타일 등록</span>
                <img src={chevironRight} alt="내 알림장 스타일 등록" />
              </button>
              {/* <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-contract-management")
                }
              >
                <span>계약 관리</span>
                <img src={chevironRight} alt="계약 관리" />
              </button> */}
              {/* 계약 관리 — 트리 드롭다운 */}
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() => setContractMenuOpen((v) => !v)}
              >
                <span>계약 관리</span>
                <span
                  className={`text-[#BDBDBD] text-[12px] transition-transform ${
                    contractMenuOpen ? "rotate-90" : ""
                  }`}
                >
                  ▶
                </span>
              </button>
              {contractMenuOpen && <ContractTreeChildren history={history} />}
              {/* <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-product-management")
                }
              >
                <span>상품 관리</span>
                <img src={chevironRight} alt="상품 관리" />
              </button> */}
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-business-time-management")
                }
              >
                <span>영업시간 관리</span>
                <img src={chevironRight} alt="영업시간 관리" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push(
                    "/business/layout/user-business-photo-management",
                  )
                }
              >
                <span>사업장 사진 등록</span>
                <img src={chevironRight} alt="사업장 사진등록" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push("/business/layout/user-subscription-management")
                }
              >
                <span>이용권 관리</span>
                <img src={chevironRight} alt="이용권 관리" />
              </button>
              <button
                className="flex justify-between items-center px-4 py-2 w-full"
                onClick={() =>
                  history.push(
                    "/business/layout/user-subscription-issue-history",
                  )
                }
              >
                <span>이용권 발급 내역</span>
                <img src={chevironRight} alt="이용권 발급 내역" />
              </button>
            </div>
            {/* 시스템 정보 */}
            <div className="mt-4">
              <div className="mb-2 font-medium">시스템 정보</div>
              <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
                <button
                  className="flex justify-between items-center px-4 py-3 w-full"
                  onClick={() =>
                    history.push("/business/layout/user-owner-announcement")
                  }
                >
                  <span>공지사항</span>
                  <div className="flex items-center gap-2">
                    {announcementUnreadCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold">
                        {announcementUnreadCount > 99
                          ? "99+"
                          : announcementUnreadCount}
                      </span>
                    )}
                    <img src={chevironRight} alt="공지사항" />
                  </div>
                </button>
                <button
                  className="flex justify-between items-center px-4 py-3 w-full"
                  onClick={() => history.push("/policy" as any)}
                >
                  <span>약관 및 정책</span>
                  <img src={chevironRight} alt="약관 및 정책" />
                </button>
              </div>
            </div>
          </div>

          {/* Dialog 모달 */}
          <Dialog
            visible={isModalVisible}
            onClose={handleModalClose}
            persist={false}
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">
                  아직 베타 서비스예요
                </div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  서비스 플랜은 아직 정해진게 없어요
                </div>
                <div className="text-sm text-center text-[#666666]">
                  편하게 무료로 이용하실 수 있어요
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                  onClick={handleModalClose}
                >
                  확인
                </button>
              </div>
            </div>
          </Dialog>

          {/* 활성화 요청 성공 모달 */}
          <Dialog
            visible={isActivationSuccessVisible}
            onClose={() => {
              setIsActivationSuccessVisible(false);
            }}
            persist={false}
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">
                  활성화 요청 완료
                </div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  {activationMessage.split("\n").map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                  onClick={() => setIsActivationSuccessVisible(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </Dialog>

          {/* 서비스 활성화 승인 모달 */}
          <Dialog
            visible={isActivationApprovedVisible}
            onClose={handleActivationApprovedConfirm}
            persist={false}
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">알림</div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  서비스 활성화가 승인되었습니다.
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                  onClick={() => void handleActivationApprovedConfirm()}
                >
                  확인
                </button>
              </div>
            </div>
          </Dialog>

          {/* 활성화 요청 실패 모달 */}
          <Dialog
            visible={isActivationErrorVisible}
            onClose={() => {
              setIsActivationErrorVisible(false);
            }}
            persist={false}
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">
                  활성화 요청 실패
                </div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  {activationMessage.split("\n").map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                  onClick={() => setIsActivationErrorVisible(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </Dialog>
        </div>
      </div>
    </>
  );
}
